import React from 'react'
import { removeFromQueue, removeApplied } from '../utils/queueStorage'

const WORK_MODE_LABELS = { remote: 'Remote', hybrid: 'Hybrid', onsite: 'On-site' }

const STATUS_CONFIG = {
  draft:     { label: 'Draft',     color: '#9a9a9a' },
  applied:   { label: 'Applied',   color: '#8888e8' },
  interview: { label: 'Interview', color: '#a088e8' },
  offer:     { label: 'Offer',     color: '#7fc97f' },
  rejected:  { label: 'Rejected',  color: '#ff8888' },
}

export function ArkHome({ queue, onQueueChange, repo, applied, onAppliedChange, onStartJob, onOpenResume, onNewResume }) {
  const repoEntries = Object.values(repo).sort((a, b) => new Date(b.updated) - new Date(a.updated))

  const handleRemoveFromQueue = (key) => {
    const next = removeFromQueue(key)
    onQueueChange(next)
  }

  const handleRemoveApplied = (id) => {
    const next = removeApplied(id)
    onAppliedChange(next)
  }

  return (
    <div className="ark-home">

      {/* ── Job Queue ── */}
      <section className="ark-home-section">
        <div className="ark-home-section-header">
          <h2 className="ark-home-section-title">Job Queue</h2>
          {queue.length > 0 && (
            <span className="ark-home-section-count">{queue.length}</span>
          )}
        </div>

        {queue.length === 0 ? (
          <div className="ark-home-empty">
            No jobs queued yet. Select roles from{' '}
            <a href="/jobs/roles" className="ark-home-link">Open Roles</a>{' '}
            and click "Send to ark".
          </div>
        ) : (
          <ul className="ark-queue-list">
            {queue.map((job) => (
              <li key={job._key} className="ark-queue-item">
                <div className="ark-queue-item-info">
                  <span className="ark-queue-title">{job.job_title.split(/[•·]/)[0].replace(/\s+(Remote|Hybrid|On-?site)(\s*[-–,/]\s*.+)?$/i, '').trim()}</span>
                  <span className="ark-queue-company">{job.company_name}</span>
                  {job.work_mode && job.work_mode !== 'unspecified' && (
                    <span className={`ark-queue-mode ark-queue-mode--${job.work_mode}`}>
                      {WORK_MODE_LABELS[job.work_mode]}
                    </span>
                  )}
                </div>
                <div className="ark-queue-item-actions">
                  {job.job_url && (
                    <a
                      href={job.job_url}
                      className="ark-queue-view-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View →
                    </a>
                  )}
                  <button
                    className="ark-queue-tailor-btn"
                    onClick={() => onStartJob(job)}
                  >
                    Tailor resume
                  </button>
                  <button
                    className="ark-queue-remove-btn"
                    onClick={() => handleRemoveFromQueue(job._key)}
                    title="Remove from queue"
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Resume Library ── */}
      <section className="ark-home-section">
        <div className="ark-home-section-header">
          <h2 className="ark-home-section-title">Resumes</h2>
          <button className="ark-home-new-btn" onClick={onNewResume}>+ New</button>
        </div>

        {repoEntries.length === 0 ? (
          <div className="ark-home-empty">
            No resumes saved yet. Tailor one from the job queue above.
          </div>
        ) : (
          <ul className="ark-resume-list">
            {repoEntries.map(entry => (
              <li key={entry.id} className="ark-resume-item" onClick={() => onOpenResume(entry)}>
                <div className="ark-resume-item-info">
                  <span className="ark-resume-title">{entry.title || 'Untitled'}</span>
                  <span className="ark-resume-meta">
                    {entry.updated ? new Date(entry.updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                  </span>
                </div>
                <span
                  className="ark-resume-status"
                  style={{ color: STATUS_CONFIG[entry.status]?.color || '#9a9a9a' }}
                >
                  {STATUS_CONFIG[entry.status]?.label || entry.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Application Tracker ── */}
      <section className="ark-home-section">
        <div className="ark-home-section-header">
          <h2 className="ark-home-section-title">Applied</h2>
          {applied.length > 0 && (
            <span className="ark-home-section-count">{applied.length}</span>
          )}
        </div>

        {applied.length === 0 ? (
          <div className="ark-home-empty">
            No applications tracked yet. After tailoring a resume, mark it as applied.
          </div>
        ) : (
          <ul className="ark-applied-list">
            {applied.map((a) => (
              <li key={a.id} className="ark-applied-item">
                <div className="ark-applied-item-info">
                  <span className="ark-applied-title">{a.jobTitle}</span>
                  <span className="ark-applied-company">{a.company}</span>
                  <span className="ark-applied-date">
                    Applied {new Date(a.appliedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <div className="ark-applied-item-right">
                  {a.resumeTitle && (
                    <span className="ark-applied-resume">
                      <span className="ark-applied-resume-label">Resume:</span> {a.resumeTitle}
                    </span>
                  )}
                  <button
                    className="ark-queue-remove-btn"
                    onClick={() => handleRemoveApplied(a.id)}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

    </div>
  )
}
